import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { useTranslation } from "react-i18next";
import { getCustomers } from "../../api/Customer/customerApi";
import {
  sortCustomersByName,
  sortProveedoresByName,
  filterOptionsStartsWith,
  buildShippingAddressOptions,
  formatEntityShippingAddress,
} from "../../utils/courierControlSelects";
import {
  getContactsByCustomerId,
  addContact,
} from "../../api/Customer/contactApi";
import {
  getProveedores,
  getContactosProveedor,
  addContactoProveedor,
} from "../../api/ProveedoresyFabricas/proveedoresApi";

export const DESTINO_CLIENTE = "CLIENTE";
export const DESTINO_PROVEEDOR = "PROVEEDOR";
export const DESTINO_INTERNO = "INTERNO";

export const emptyEnvioDestino = () => ({
  TIPO_DESTINO: "",
  ID_CLIENTE: null,
  CODPROV: null,
  ID_CONTACTO: null,
  PERSONA_ENVIO: "",
  DIRECCION_ENVIO: "",
  ID_Marca: null,
});

export const isEnvioDestinoValid = (
  value = {},
  { requireContact = true } = {},
) => {
  const tipo = String(value.TIPO_DESTINO || "").toUpperCase();
  if (!tipo) {
    return Boolean(value.ID_Marca);
  }
  if (tipo === DESTINO_INTERNO) return true;
  if (tipo === DESTINO_CLIENTE) {
    if (!value.ID_CLIENTE) return false;
    return requireContact
      ? Boolean(value.ID_CONTACTO || value.PERSONA_ENVIO)
      : true;
  }
  if (tipo === DESTINO_PROVEEDOR) {
    if (!value.CODPROV) return false;
    return requireContact
      ? Boolean(value.ID_CONTACTO || value.PERSONA_ENVIO)
      : true;
  }
  return false;
};

export const toMovimientoDestinoPayload = (value = {}) => {
  const tipo = String(value.TIPO_DESTINO || "").toUpperCase() || null;
  return {
    TIPO_DESTINO: tipo,
    ID_CLIENTE: tipo === DESTINO_CLIENTE ? value.ID_CLIENTE || null : null,
    CODPROV: tipo === DESTINO_PROVEEDOR ? value.CODPROV || null : null,
    ID_CONTACTO:
      tipo === DESTINO_CLIENTE || tipo === DESTINO_PROVEEDOR
        ? value.ID_CONTACTO || null
        : value.ID_CONTACTO || null,
    PERSONA_ENVIO: value.PERSONA_ENVIO?.trim() || null,
    ID_Marca: tipo ? null : value.ID_Marca || null,
  };
};

const contactLabelCliente = (c) =>
  [c?.firstName, c?.lastName].filter(Boolean).join(" ").trim() ||
  c?.email ||
  `#${c?.id}`;

const contactLabelProveedor = (c) =>
  c?.Nombre?.trim() || c?.Email || `#${c?.id_Contacto}`;

export default function EnvioDestinoFields({
  value,
  onChange,
  disabled = false,
  allowedTipos = [DESTINO_CLIENTE, DESTINO_PROVEEDOR, DESTINO_INTERNO],
  compact = false,
  fieldSx = {},
  legacyMarcaName = "",
  internoLabel = null,
}) {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    Nombre: "",
    phone: "",
    email: "",
  });

  const tipo = String(value?.TIPO_DESTINO || "").toUpperCase();

  const allowedTiposKey = Array.isArray(allowedTipos)
    ? allowedTipos.join("|")
    : "";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingEntities(true);
      try {
        const tipos = allowedTiposKey ? allowedTiposKey.split("|") : [];
        const needsCustomers = tipos.includes(DESTINO_CLIENTE);
        const needsProveedores = tipos.includes(DESTINO_PROVEEDOR);
        const [cust, prov] = await Promise.all([
          needsCustomers ? getCustomers().catch(() => []) : Promise.resolve([]),
          needsProveedores
            ? getProveedores().catch(() => [])
            : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setCustomers(sortCustomersByName(Array.isArray(cust) ? cust : []));
        setProveedores(sortProveedoresByName(Array.isArray(prov) ? prov : []));
      } finally {
        if (!cancelled) setLoadingEntities(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [allowedTiposKey]);

  const loadContacts = useCallback(async () => {
    if (tipo === DESTINO_CLIENTE && value?.ID_CLIENTE) {
      setLoadingContacts(true);
      try {
        const list = await getContactsByCustomerId(value.ID_CLIENTE);
        setContacts(Array.isArray(list) ? list : []);
      } catch {
        setContacts([]);
      } finally {
        setLoadingContacts(false);
      }
      return;
    }
    if (tipo === DESTINO_PROVEEDOR && value?.CODPROV) {
      setLoadingContacts(true);
      try {
        const list = await getContactosProveedor(value.CODPROV);
        setContacts(Array.isArray(list) ? list : []);
      } catch {
        setContacts([]);
      } finally {
        setLoadingContacts(false);
      }
      return;
    }
    setContacts([]);
  }, [tipo, value?.ID_CLIENTE, value?.CODPROV]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const patch = (partial) => {
    onChange({ ...(value || emptyEnvioDestino()), ...partial });
  };

  const selectedCustomer = useMemo(
    () =>
      customers.find((c) => Number(c.id) === Number(value?.ID_CLIENTE)) || null,
    [customers, value?.ID_CLIENTE],
  );

  const selectedProveedor = useMemo(
    () =>
      proveedores.find((p) => Number(p.CODPROV) === Number(value?.CODPROV)) ||
      null,
    [proveedores, value?.CODPROV],
  );

  const shippingAddressOptions = useMemo(() => {
    if (tipo === DESTINO_CLIENTE && selectedCustomer) {
      return buildShippingAddressOptions(selectedCustomer, {
        tipo: DESTINO_CLIENTE,
      });
    }
    if (tipo === DESTINO_PROVEEDOR && selectedProveedor) {
      return buildShippingAddressOptions(selectedProveedor, {
        tipo: DESTINO_PROVEEDOR,
      });
    }
    return [];
  }, [tipo, selectedCustomer, selectedProveedor]);

  const selectedContact = useMemo(() => {
    const persona = String(value?.PERSONA_ENVIO || "")
      .trim()
      .toLowerCase();
    if (tipo === DESTINO_CLIENTE) {
      if (value?.ID_CONTACTO) {
        const byId = contacts.find(
          (c) => Number(c.id) === Number(value.ID_CONTACTO),
        );
        if (byId) return byId;
      }
      if (!persona) return null;
      return (
        contacts.find(
          (c) => contactLabelCliente(c).trim().toLowerCase() === persona,
        ) || null
      );
    }
    if (tipo === DESTINO_PROVEEDOR) {
      if (value?.ID_CONTACTO) {
        const byId = contacts.find(
          (c) => Number(c.id_Contacto) === Number(value.ID_CONTACTO),
        );
        if (byId) return byId;
      }
      if (!persona) return null;
      return (
        contacts.find(
          (c) => contactLabelProveedor(c).trim().toLowerCase() === persona,
        ) || null
      );
    }
    return null;
  }, [contacts, value?.ID_CONTACTO, value?.PERSONA_ENVIO, tipo]);

  // Al reabrir: Contacto se guardó como texto; recuperar ID_CONTACTO al cargar lista
  useEffect(() => {
    if (!selectedContact || value?.ID_CONTACTO) return;
    if (tipo === DESTINO_CLIENTE && selectedContact.id != null) {
      patch({
        ID_CONTACTO: selectedContact.id,
        PERSONA_ENVIO:
          String(value?.PERSONA_ENVIO || "").trim() ||
          contactLabelCliente(selectedContact),
      });
      return;
    }
    if (tipo === DESTINO_PROVEEDOR && selectedContact.id_Contacto != null) {
      patch({
        ID_CONTACTO: selectedContact.id_Contacto,
        PERSONA_ENVIO:
          String(value?.PERSONA_ENVIO || "").trim() ||
          contactLabelProveedor(selectedContact),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContact, value?.ID_CONTACTO, tipo]);

  const handleTipoChange = (nextTipo) => {
    patch({
      TIPO_DESTINO: nextTipo,
      ID_CLIENTE: null,
      CODPROV: null,
      ID_CONTACTO: null,
      PERSONA_ENVIO: "",
      ID_Marca: null,
    });
  };

  const openAddContact = () => {
    setNewContact({
      firstName: "",
      lastName: "",
      Nombre: "",
      phone: "",
      email: "",
    });
    setAddOpen(true);
  };

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      if (tipo === DESTINO_CLIENTE && value?.ID_CLIENTE) {
        const created = await addContact(value.ID_CLIENTE, {
          firstName: newContact.firstName.trim(),
          lastName: newContact.lastName.trim(),
          phone: newContact.phone.trim() || null,
          email: newContact.email.trim() || null,
        });
        await loadContacts();
        const label = contactLabelCliente(created);
        patch({
          ID_CONTACTO: created.id,
          PERSONA_ENVIO: label,
        });
      } else if (tipo === DESTINO_PROVEEDOR && value?.CODPROV) {
        const created = await addContactoProveedor(value.CODPROV, {
          Nombre: newContact.Nombre.trim(),
          Telefono: newContact.phone.trim() || null,
          Email: newContact.email.trim() || null,
          Activo: true,
        });
        await loadContacts();
        const label = contactLabelProveedor(created);
        patch({
          ID_CONTACTO: created.id_Contacto,
          PERSONA_ENVIO: label,
        });
      }
      setAddOpen(false);
    } catch (err) {
      console.error("Error creando contacto:", err);
    } finally {
      setSavingContact(false);
    }
  };

  const canAddContact =
    (tipo === DESTINO_CLIENTE && value?.ID_CLIENTE) ||
    (tipo === DESTINO_PROVEEDOR && value?.CODPROV);

  const addContactValid =
    tipo === DESTINO_CLIENTE
      ? Boolean(newContact.firstName.trim() && newContact.lastName.trim())
      : Boolean(newContact.Nombre.trim());

  const gap = compact ? 1 : 1.5;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: compact ? "row" : "column",
        flexWrap: compact ? "wrap" : "nowrap",
        gap,
        width: "100%",
        minWidth: 0,
        alignItems: compact ? "center" : "stretch",
      }}
    >
      {!tipo && value?.ID_Marca && legacyMarcaName ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ width: "100%", mb: compact ? 0 : 0.5 }}
        >
          {t("Samples.legacyMarcaDestination", {
            name: legacyMarcaName,
          })}
        </Typography>
      ) : null}

      <FormControl
        size="small"
        disabled={disabled}
        sx={{
          ...fieldSx,
          flex: compact ? "1 1 120px" : undefined,
          minWidth: compact ? 120 : undefined,
        }}
      >
        <InputLabel>{t("Samples.destinationType")}</InputLabel>
        <Select
          label={t("Samples.destinationType")}
          value={tipo || ""}
          onChange={(e) => handleTipoChange(e.target.value)}
        >
          {allowedTipos.includes(DESTINO_CLIENTE) && (
            <MenuItem value={DESTINO_CLIENTE}>
              {t("Samples.destinationClient")}
            </MenuItem>
          )}
          {allowedTipos.includes(DESTINO_PROVEEDOR) && (
            <MenuItem value={DESTINO_PROVEEDOR}>
              {t("Samples.destinationSupplier")}
            </MenuItem>
          )}
          {allowedTipos.includes(DESTINO_INTERNO) && (
            <MenuItem value={DESTINO_INTERNO}>
              {internoLabel || t("Samples.destinationInternal")}
            </MenuItem>
          )}
        </Select>
      </FormControl>

      {tipo === DESTINO_CLIENTE && (
        <Autocomplete
          size="small"
          disabled={disabled || loadingEntities}
          options={customers}
          value={selectedCustomer}
          getOptionLabel={(opt) => opt?.name || ""}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          filterOptions={(opts, state) =>
            filterOptionsStartsWith(opts, state, (o) => o?.name || "")
          }
          onChange={(_, opt) => {
            // Solo al cambiar de cliente: dirección de ESA ficha (no arrastrar la anterior)
            const nextDir = opt
              ? formatEntityShippingAddress(opt, DESTINO_CLIENTE)
              : "";
            patch({
              ID_CLIENTE: opt?.id ?? null,
              ID_CONTACTO: null,
              PERSONA_ENVIO: "",
              CODPROV: null,
              ID_Marca: null,
              DIRECCION_ENVIO: nextDir,
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Samples.destinationClient")}
              sx={fieldSx}
            />
          )}
          sx={{
            flex: compact ? "1.4 1 160px" : undefined,
            minWidth: compact ? 160 : undefined,
          }}
        />
      )}

      {tipo === DESTINO_PROVEEDOR && (
        <Autocomplete
          size="small"
          disabled={disabled || loadingEntities}
          options={proveedores}
          value={selectedProveedor}
          getOptionLabel={(opt) => opt?.N_PROVEEDOR || ""}
          isOptionEqualToValue={(a, b) =>
            Number(a?.CODPROV) === Number(b?.CODPROV)
          }
          filterOptions={(opts, state) =>
            filterOptionsStartsWith(opts, state, (o) => o?.N_PROVEEDOR || "")
          }
          onChange={(_, opt) => {
            const nextDir = opt
              ? formatEntityShippingAddress(opt, DESTINO_PROVEEDOR)
              : "";
            patch({
              CODPROV: opt?.CODPROV ?? null,
              ID_CONTACTO: null,
              PERSONA_ENVIO: "",
              ID_CLIENTE: null,
              ID_Marca: null,
              DIRECCION_ENVIO: nextDir,
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Samples.destinationSupplier")}
              sx={fieldSx}
            />
          )}
          sx={{
            flex: compact ? "1.4 1 160px" : undefined,
            minWidth: compact ? 160 : undefined,
          }}
        />
      )}

      {tipo === DESTINO_INTERNO && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flex: compact ? "1 1 140px" : undefined,
            py: compact ? 0 : 0.5,
          }}
        >
          {t("Samples.destinationInternalHint")}
        </Typography>
      )}

      {(tipo === DESTINO_CLIENTE || tipo === DESTINO_PROVEEDOR) && (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
            flex: compact ? "1.5 1 180px" : undefined,
            minWidth: compact ? 180 : undefined,
            width: compact ? undefined : "100%",
          }}
        >
          <Autocomplete
            size="small"
            fullWidth
            disabled={disabled || loadingContacts || !canAddContact}
            options={contacts}
            value={selectedContact}
            getOptionLabel={(opt) =>
              tipo === DESTINO_CLIENTE
                ? contactLabelCliente(opt)
                : contactLabelProveedor(opt)
            }
            isOptionEqualToValue={(a, b) =>
              tipo === DESTINO_CLIENTE
                ? Number(a?.id) === Number(b?.id)
                : Number(a?.id_Contacto) === Number(b?.id_Contacto)
            }
            onChange={(_, opt) => {
              if (!opt) {
                patch({ ID_CONTACTO: null, PERSONA_ENVIO: "" });
                return;
              }
              if (tipo === DESTINO_CLIENTE) {
                patch({
                  ID_CONTACTO: opt.id,
                  PERSONA_ENVIO: contactLabelCliente(opt),
                });
              } else {
                patch({
                  ID_CONTACTO: opt.id_Contacto,
                  PERSONA_ENVIO: contactLabelProveedor(opt),
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Samples.shipmentPerson")}
                sx={fieldSx}
              />
            )}
          />
          <IconButton
            size="small"
            color="primary"
            disabled={disabled || !canAddContact}
            onClick={openAddContact}
            aria-label={t("Samples.addContact")}
          >
            <PersonAddAlt1OutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {(tipo === DESTINO_CLIENTE || tipo === DESTINO_PROVEEDOR) &&
      (selectedCustomer || selectedProveedor) ? (
        <Autocomplete
          freeSolo
          size="small"
          disabled={disabled}
          options={shippingAddressOptions}
          value={value?.DIRECCION_ENVIO || ""}
          filterOptions={(opts, state) =>
            filterOptionsStartsWith(opts, state, (o) =>
              typeof o === "string" ? o : String(o || ""),
            )
          }
          onChange={(_, next) =>
            patch({
              DIRECCION_ENVIO:
                typeof next === "string" ? next : String(next || ""),
            })
          }
          onInputChange={(_, next, reason) => {
            if (reason === "input" || reason === "clear") {
              patch({ DIRECCION_ENVIO: next || "" });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Samples.shipmentAddress")}
              helperText={t("Samples.shipmentAddressHint")}
              sx={fieldSx}
            />
          )}
          sx={{
            flex: compact ? "1 1 100%" : undefined,
            minWidth: compact ? 200 : undefined,
            width: compact ? undefined : "100%",
          }}
        />
      ) : null}

      <Dialog
        open={addOpen}
        onClose={() => !savingContact && setAddOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("Samples.addContactTitle")}</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}
        >
          {tipo === DESTINO_CLIENTE ? (
            <>
              <TextField
                label={t("Samples.contactFirstName")}
                value={newContact.firstName}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                fullWidth
                autoFocus
              />
              <TextField
                label={t("Samples.contactLastName")}
                value={newContact.lastName}
                onChange={(e) =>
                  setNewContact((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
                fullWidth
              />
            </>
          ) : (
            <TextField
              label={t("Samples.contactName")}
              value={newContact.Nombre}
              onChange={(e) =>
                setNewContact((prev) => ({ ...prev, Nombre: e.target.value }))
              }
              fullWidth
              autoFocus
            />
          )}
          <TextField
            label={t("Samples.contactPhone")}
            value={newContact.phone}
            onChange={(e) =>
              setNewContact((prev) => ({ ...prev, phone: e.target.value }))
            }
            fullWidth
          />
          <TextField
            label={t("Samples.contactEmail")}
            value={newContact.email}
            onChange={(e) =>
              setNewContact((prev) => ({ ...prev, email: e.target.value }))
            }
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} disabled={savingContact}>
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveContact}
            disabled={savingContact || !addContactValid}
          >
            {t("Samples.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
